import React, { useState, useEffect, useRef } from "react";
import {
  GenericTable,
  type GenericTableColumn,
} from "@/styles/GenericTable";
import { GenericForm, type GenericFormField } from "@/styles/GenericForm";
import { type GenericAttachment } from "@/styles/GenericAttachmentsSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { ApiError, apiRequest, apiUpload } from "@/lib/api-request";
import { useUser } from "@/contexts/UserContext";
import { useAdminGuard } from "../AdminOnlyGuard";
import { getApiUrl } from "@/lib/api-config";
import {
  MAX_LOGO_FILE_BYTES,
  ALLOWED_LOGO_MIME_TYPES,
  RASTER_LOGO_MIME_TYPES,
  MAX_LOGO_DIMENSION_PX,
  LOGO_UPDATED_EVENT,
  getImageDimensions,
  formatFileSize,
} from "@/lib/file-upload";
import { AddressAutocomplete, type AddressSelection } from "@/features/project/info/AddressAutocomplete";
import { setHasUnsavedChanges } from "@/lib/unsaved-changes";
import { WorkingDaysSection } from "./WorkingDaysSection";
import { TimezoneSection } from "./TimezoneSection";

/**
 * Company management -- pre-existing Settings functionality, unchanged.
 * Not one of the 4 tabs named in the Phase 4 spec (Account/Usage/
 * Notifications/Plans), but this is what the old un-tabbed Settings page
 * did and there's no instruction to remove it, so it's kept as-is under
 * its own tab rather than deleted.
 */

type CompanyRow = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip_code: string | null;
  website: string | null;
  status: string;
  created_at: string;
  updated_at: string;
};

const columns: GenericTableColumn<CompanyRow>[] = [
  {
    key: "name",
    header: "Company Name",
    sortable: true,
    width: '15rem',
    render: (row) => (
      <span className="font-medium">{row.name}</span>
    ),
  },
  {
    key: "phone",
    header: "Phone",
    sortable: true,
    width: '12rem',
    render: (row) => row.phone || <span className="text-gray-400">—</span>,
  },
  {
    key: "address",
    header: "Address",
    sortable: true,
    width: '15rem',
    render: (row) => row.address || <span className="text-gray-400">—</span>,
  },
  {
    key: "website",
    header: "Website",
    sortable: true,
    width: '12rem',
    render: (row) => row.website || <span className="text-gray-400">—</span>,
  },
  {
    key: "status",
    header: "Status",
    align: "center",
    sortable: true,
    width: '8rem',
    render: (row) => (
      <span
        className={`
          inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium
          ${
            row.status === "active"
              ? "bg-green-50 text-green-700"
              : "bg-gray-100 text-gray-600"
          }
        `}
      >
        {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
      </span>
    ),
    sortValue: (row) => row.status,
  },
  {
    key: "updated_at",
    header: "Last updated",
    align: "right",
    width: '11.25rem',
    sortable: true,
    render: (row) =>
      new Date(row.updated_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    sortValue: (row) => new Date(row.updated_at).getTime(),
  },
];

// Form fields configuration for company.
// Deliberately NOT here:
//  - email: a company's business email is not the same thing as anyone's
//    personal signup email, and isn't user-editable from this form at all
//    (phase61_company_email_optional.sql).
//  - status: backend-only (set at creation by onboard_company(), never
//    client-editable) -- stripped server-side too, see
//    CLIENT_DENY_FIELDS in modules/companies/service.js, not just hidden
//    here.
//  - address/city/state/country/zip_code: GenericForm's declarative fields
//    don't support an embedded live component. Rendered as a custom
//    section (AddressAutocomplete, same one used in
//    project/info/ProjectForm.tsx) below, via `additionalSections`, and
//    merged into the same PUT on submit. See addressFields state below.
const formFields: GenericFormField[] = [
  {
    name: 'name',
    label: 'Company Name',
    type: 'text',
    required: true,
    row: 1,
    placeholder: 'Enter company name'
  },
  {
    name: 'website',
    label: 'Website',
    type: 'text',
    required: false,
    row: 2,
    placeholder: 'https://www.example.com'
  },
  {
    name: 'phone',
    label: 'Phone',
    type: 'text',
    required: false,
    row: 2,
    placeholder: '(555) 123-4567'
  },
];


interface AddressFieldsState {
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
}

const EMPTY_ADDRESS_FIELDS: AddressFieldsState = { address: '', city: '', state: '', country: '', zip_code: '' };

export function CompanyTab() {
  const { user, companyId, companyAddressMissing, refreshUser } = useUser();
  const canApply = useAdminGuard();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<CompanyRow | null>(null);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [attachments, setAttachments] = useState<GenericAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addressFields, setAddressFields] = useState<AddressFieldsState>(EMPTY_ADDRESS_FIELDS);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const hasAutoOpenedRef = useRef(false);

  // Fetch companies
  useEffect(() => {
    if (companyId) {
      fetchCompanies();
    }
  }, [companyId]);

  // Fetch the logo -- independent of the Edit Company modal (it lives
  // directly on the page, not inside additionalSections) so it loads as
  // soon as the company itself is known, whether or not the user ever
  // opens the edit form.
  const activeCompanyId = companies[0]?.id || null;
  useEffect(() => {
    if (activeCompanyId) {
      fetchLogos(activeCompanyId);
    } else {
      setAttachments([]);
    }
  }, [activeCompanyId]);

  // Keep the address section's local state in sync with whichever row the
  // form is currently editing (mirrors GenericForm's own initialValues
  // sync, needed here separately since address isn't one of its
  // declarative fields).
  useEffect(() => {
    setAddressFields(
      selectedRow
        ? {
            address: selectedRow.address || '',
            city: selectedRow.city || '',
            state: selectedRow.state || '',
            country: selectedRow.country || '',
            zip_code: selectedRow.zip_code || '',
          }
        : EMPTY_ADDRESS_FIELDS
    );
  }, [selectedRow]);

  // App.tsx's route guard sends a companyAddressMissing user straight to
  // this tab (SettingsPage defaults activeTab to "company" for them) --
  // open the edit form immediately instead of making them also find and
  // click their own company row.
  useEffect(() => {
    if (companyAddressMissing && companies.length === 1 && !hasAutoOpenedRef.current && !isFormOpen) {
      hasAutoOpenedRef.current = true;
      setSelectedRow(companies[0]);
      setIsFormOpen(true);
    }
  }, [companyAddressMissing, companies, isFormOpen]);

  const fetchCompanies = async () => {
    if (!companyId) return;

    try {
      setIsLoading(true);
      // the company's own record, from the backend (GET /api/company/profile)
      const company = await apiRequest<any>('api/company/profile');

      setCompanies(company ? [company] : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressSelect = (selection: AddressSelection) => {
    setAddressFields((prev) => ({
      ...prev,
      address: selection.addressLine1,
      city: selection.city || prev.city,
      state: selection.state || prev.state,
      zip_code: selection.postalCode || prev.zip_code,
      country: selection.country || prev.country,
    }));
  };

  const fetchLogos = async (companyId: string) => {
    try {
      // the active logos, newest first, each with a usable public URL (GET /api/company/logos)
      const data = await apiRequest<any[]>('api/company/logos');

      const logoAttachments: GenericAttachment[] = (data || []).map((logo) => {
        const publicUrl = logo.public_url;

        return {
          id: logo.id,
          file_name: logo.file_name,
          file_type: logo.mime_type,
          file_size: logo.size_bytes,
          file_url: publicUrl || '',
          created_at: logo.created_at,
          created_by: logo.uploaded_by,
        };
      });

      setAttachments(logoAttachments);
    } catch (error) {
      console.error('Error fetching logos:', error);
      setAttachments([]);
    }
  };

  const handleRowClick = (row: CompanyRow) => {
    setSelectedRow(row);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    if (!user || !companyId || !selectedRow) return;
    if (!canApply()) return;

    try {
      // Update company via generic API -- merges the declarative
      // GenericForm fields (name/email/phone/website/status) with the
      // custom address section's own local state (address/city/state/
      // country/zip_code), one save, one PUT.
      const response = await fetch(getApiUrl(`api/entities/company/${selectedRow.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          address: addressFields.address.trim() || null,
          city: addressFields.city.trim() || null,
          state: addressFields.state.trim() || null,
          country: addressFields.country.trim() || null,
          zip_code: addressFields.zip_code.trim() || null,
          updated_at: new Date().toISOString(),
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update company');
      }

      await fetchCompanies();
      // Re-resolve companyAddressMissing app-wide -- this is what lets
      // App.tsx's route guard release the user from this tab once they've
      // actually filled in an address.
      await refreshUser();
      setIsFormOpen(false);
      setSelectedRow(null);
    } catch (error) {
      console.error('Error updating company:', error);
      alert('Failed to update company. Please try again.');
    }
  };

  const handleFormCancel = () => {
    // Locked (address still missing): closing the form would strand the
    // user on this tab with no way to reopen it except reloading -- keep
    // it open. Once address is filled, Cancel behaves normally again.
    if (companyAddressMissing) return;
    setIsFormOpen(false);
    setSelectedRow(null);
  };

  const handleUploadFiles = async (files: File[]): Promise<boolean> => {
    if (!user || !companyId || !activeCompanyId) return false;
    if (!canApply()) return false;
    setLogoError(null);

    let uploadedAny = false;

    for (const file of files) {
      try {
        if (!(file instanceof File)) {
          continue;
        }

        if (!ALLOWED_LOGO_MIME_TYPES.includes(file.type)) {
          setLogoError('Logo must be a PNG, JPG, WEBP, or SVG file.');
          continue;
        }

        if (file.size <= 0 || file.size > MAX_LOGO_FILE_BYTES) {
          setLogoError(`Logo must be ${formatFileSize(MAX_LOGO_FILE_BYTES)} or smaller.`);
          continue;
        }

        // SVG is vector -- no fixed raster resolution to check.
        if (RASTER_LOGO_MIME_TYPES.includes(file.type)) {
          try {
            const { width, height } = await getImageDimensions(file);
            if (width > MAX_LOGO_DIMENSION_PX || height > MAX_LOGO_DIMENSION_PX) {
              setLogoError(`Logo must be ${MAX_LOGO_DIMENSION_PX}x${MAX_LOGO_DIMENSION_PX} pixels or smaller.`);
              continue;
            }
          } catch {
            setLogoError('Could not read the image file. Please try a different file.');
            continue;
          }
        }

        // The server validates the file again, stores it and makes it the company's active logo (the previous ones are deactivated there).
        const form = new FormData();
        form.append('file_name', file.name);
        form.append('file', file, file.name);
        await apiUpload('api/company/logos', form);
        uploadedAny = true;
      } catch (error) {
        console.error('Error uploading logo:', error);
        setLogoError(error instanceof ApiError ? error.message : 'Failed to upload logo. Please try again.');
      }
    }

    if (uploadedAny) {
      await fetchLogos(activeCompanyId);
      // Tell the already-mounted sidebar's CompanyLogoBox to refetch --
      // it has no other way to learn about this from way over here.
      window.dispatchEvent(new CustomEvent(LOGO_UPDATED_EVENT));
    }
    return uploadedAny;
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!user || !companyId || !activeCompanyId) return;
    if (!canApply()) return;
    setLogoError(null);

    try {
      // Delete via generic API (set is_active to false)
      const response = await fetch(getApiUrl(`api/entities/companies_logos/${attachmentId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete logo');
      }

      // Refresh logos
      await fetchLogos(activeCompanyId);
      window.dispatchEvent(new CustomEvent(LOGO_UPDATED_EVENT));
    } catch (error) {
      console.error('Error deleting logo:', error);
      setLogoError('Failed to delete logo. Please try again.');
    }
  };

  const openLogoModal = () => {
    setLogoError(null);
    setIsLogoModalOpen(true);
  };

  const handleLogoFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file) return;
    const success = await handleUploadFiles([file]);
    if (success) setIsLogoModalOpen(false);
  };

  const handleLogoDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const success = await handleUploadFiles([file]);
    if (success) setIsLogoModalOpen(false);
  };

  const handleLogoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[24rem]">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6 w-1/4 min-w-[12rem]">
        <h3 className="font-medium mb-2">Company Logo</h3>
        <div className="rounded-lg border border-gray-200 p-3 flex flex-col items-center gap-3">
          {attachments[0] ? (
            <img
              src={attachments[0].file_url}
              alt="Company logo"
              className="h-24 w-24 object-contain rounded"
            />
          ) : (
            <div className="h-24 w-24 flex items-center justify-center rounded bg-gray-50 text-gray-300">
              <ImageIcon className="h-10 w-10" />
            </div>
          )}
          <Button type="button" variant="outline" size="sm" onClick={openLogoModal} className="w-full">
            {attachments[0] ? 'Change Logo' : 'Upload your Logo'}
          </Button>
        </div>
      </div>

      <Dialog open={isLogoModalOpen} onOpenChange={setIsLogoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload your Logo</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-gray-500">
            PNG, JPG, WEBP, or SVG. Max {formatFileSize(MAX_LOGO_FILE_BYTES)}. Max size: 400x400px.
          </p>

          {logoError && <div className="error-message" role="alert">{logoError}</div>}

          <div
            onClick={() => logoInputRef.current?.click()}
            onDrop={handleLogoDrop}
            onDragOver={handleLogoDragOver}
            onDragLeave={handleLogoDragLeave}
            className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 cursor-pointer transition-colors ${
              isDraggingLogo ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Upload className="h-8 w-8 text-gray-400" />
            <p className="text-sm font-medium text-gray-700">Drag and drop your logo here</p>
            <p className="text-sm text-gray-500">or click to browse files</p>
            <input
              ref={logoInputRef}
              type="file"
              accept={ALLOWED_LOGO_MIME_TYPES.join(',')}
              onChange={handleLogoFileInputChange}
              className="hidden"
            />
          </div>

          {attachments[0] && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleDeleteAttachment(attachments[0].id)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Remove current logo
            </Button>
          )}
        </DialogContent>
      </Dialog>

      <GenericTable
        columns={columns}
        data={companies}
        rowKey={(row) => row.id}
        defaultSort={{ columnKey: "name", direction: "asc" }}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={960}
        onRowClick={handleRowClick}
        loading={isLoading}
        emptyState="No companies found"
      />

      <TimezoneSection />
      <WorkingDaysSection companyId={companyId} userId={user?.id} />

      <GenericForm
        title="Company"
        mode={selectedRow ? "edit" : "create"}
        fields={formFields}
        initialValues={selectedRow || {}}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        isOpen={isFormOpen}
        onDirtyChange={setHasUnsavedChanges}
        additionalSections={
          selectedRow && (
            <>
              {companyAddressMissing && (
                <div className="error-message mb-4" role="alert">
                  Please add your company address to continue using KrewUp.
                </div>
              )}
              <div className="form-group">
                <label htmlFor="company-address" className="form-label">Address</label>
                <AddressAutocomplete
                  value={addressFields.address}
                  onChange={(value) => setAddressFields((prev) => ({ ...prev, address: value }))}
                  onSelectSuggestion={handleAddressSelect}
                  placeholder="Search address"
                  initialSelected={!!addressFields.address}
                />
              </div>
              {/* the same field look as every GenericForm field (Input + font-form-input), two to a row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="company-city" className="font-form-label">City</Label>
                  <Input id="company-city" className="font-form-input generic-form-field" value={addressFields.city}
                         onChange={(e) => setAddressFields((prev) => ({ ...prev, city: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-state" className="font-form-label">State</Label>
                  <Input id="company-state" className="font-form-input generic-form-field" value={addressFields.state}
                         onChange={(e) => setAddressFields((prev) => ({ ...prev, state: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-country" className="font-form-label">Country</Label>
                  <Input id="company-country" className="font-form-input generic-form-field" value={addressFields.country}
                         onChange={(e) => setAddressFields((prev) => ({ ...prev, country: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-zip" className="font-form-label">ZIP Code</Label>
                  <Input id="company-zip" className="font-form-input generic-form-field" value={addressFields.zip_code}
                         onChange={(e) => setAddressFields((prev) => ({ ...prev, zip_code: e.target.value }))} />
                </div>
              </div>
            </>
          )
        }
      />
    </>
  );
}
